import { useEffect, useRef, useState } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileEncode from "filepond-plugin-file-encode";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFilePoster from 'filepond-plugin-file-poster';
import { FilePondFile, FileOrigin } from 'filepond';
import 'filepond/dist/filepond.min.css';
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import 'filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css';
import './FilepondUploader.css';
import { UrlConfig } from 'config';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileEncode,
  FilePondPluginFileValidateType,
  FilePondPluginFilePoster
);

type ReactFilePond = InstanceType<typeof FilePond>;

export interface FileUploaderProps {
  maxFiles?: number;
  label?: string;
  showBrowseInLabel?: boolean;
  initialFileUrls?: string[];
  onFileProcess?: (url: string) => void;
  onFileRevert?: (url: string) => void;
  onUploadingChange?: (isUploading: boolean) => void;
}

export default function FilePondUploader({
  initialFileUrls = [],
  onFileProcess,
  onFileRevert,
  onUploadingChange,
  maxFiles = 3,
  label = 'Drag & Drop your files',
  showBrowseInLabel = true
}: FileUploaderProps) {
  const pondRef = useRef<ReactFilePond | null>(null);
  const [isAtLimit, setIsAtLimit] = useState(false);
  const className = maxFiles > 1 ? 'filepond-multi-uploader' : '';
  const uploadingFiles = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pond = pondRef.current
    if (!pond) return

    const existing = new Set(
      pond.getFiles().map(f => f.serverId as string)
    )

    initialFileUrls.forEach(url => {
      if (!existing.has(url)) {
        pond.addFile(url, {
          type: 'local',
          metadata: { poster: url }
        })
      }
    })

    setIsAtLimit(pond.getFiles().length >= maxFiles);
  }, []);

  const handleUpdateFiles = (files: FilePondFile[]) => {
    setIsAtLimit(files.length >= maxFiles);
  };

  const addFileToUploadingQueue = (file: FilePondFile) => {
    if (file.origin === FileOrigin.INPUT) {
      uploadingFiles.current.add(file.id);
      onUploadingChange?.(true);
    }
  }

  const removeFileFromUploadingQueue = (file: FilePondFile | undefined) => {
    if (file) {
      uploadingFiles.current.delete(file.id);
      if (uploadingFiles.current.size === 0) {
        onUploadingChange?.(false);
      }
    }
  }

  return (
    <FilePond
      ref={pondRef}
      className={className}
      allowMultiple={maxFiles > 1}
      maxFiles={maxFiles}
      acceptedFileTypes={['image/*']}
      server={{
        url: UrlConfig.UPLOAD.FILEPOD_IMAGE,
        process: {
          url: '',
          onload: response => {
            const url = JSON.parse(response).data as string;
            onFileProcess?.(url);
            return url;
          }
        },
        revert: (uniqueFileId, load, error) => {
          fetch(UrlConfig.UPLOAD.FILEPOD_IMAGE_DELETE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: uniqueFileId }),
          })
            .then(res => res.json())
            .then(({ ok, message }) => {
              if (ok) {
                load();                 // tell FilePond delete succeeded
              } else {
                error(message || 'Server error');
              }
            })
            .catch(err => {
              console.error('Revert error:', err);
              error(err.message);
            });
        },
        load: (source, load, error, progress) => {
          fetch(source)
            .then(res => res.blob())
            .then(blob => {
              progress(true, blob.size, blob.size);
              load(blob);
            })
            .catch(err => {
              console.error('FilePond load error', err);
              error('Could not load file');
            });
        }
      }}
      name="file"
      labelIdle={
        isAtLimit
          ? "Upload limit reached"
          : showBrowseInLabel
            ? `${label} or <span class="filepond--label-action">Browse</span>`
            : label
      }
      onremovefile={(_error, file) => {
        const url = file.serverId as string;
        onFileRevert?.(url);
        handleUpdateFiles(pondRef.current!.getFiles());
      }}
      onaddfilestart={addFileToUploadingQueue}
      onprocessfile={(_, file) => removeFileFromUploadingQueue(file)}
      onerror={(_, file) => removeFileFromUploadingQueue(file)}
      onupdatefiles={handleUpdateFiles}

    />
  );
}